import { IObstetricaLabResultOption, IObstetricaLabResultsGroup, LabObservationObstetricalFile } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";

interface ObstetricalFileLabObservationsDetailsProps {
    id: string;
    observationGroup: IObstetricaLabResultsGroup;
    finalEDB: Date; // using FinalEDB will account for the calculation method when calculating trimester
    import: (value: LabObservationObstetricalFile, options: IObstetricaLabResultOption, trimester?: number) => void;
}

class ObstetricalFileLabObservationsDetailState {
    public showMore: boolean;

    public constructor(fields?: Partial<ObstetricalFileLabObservationsDetailState>) {
        Object.assign(this, fields);
    }

    public static getDefault(): ObstetricalFileLabObservationsDetailState {
        return new ObstetricalFileLabObservationsDetailState({
            showMore: false
        });
    }
}

class ObstetricalFileLabObservationsDetails extends BaseReactComponent<ObstetricalFileLabObservationsDetailsProps, ObstetricalFileLabObservationsDetailState> {
    constructor(props: ObstetricalFileLabObservationsDetailsProps) {
        super(props);
        this.state = ObstetricalFileLabObservationsDetailState.getDefault();
    }

    public render(): JSX.Element {
        const key = this.props.observationGroup.key;
        const options = this.props.observationGroup.options;
        const labObservations = this.props.observationGroup.values;

        const name = labObservations.firstOrDefault()?.ObservationStandardShortDescription ?? key;

        // display only the latest value by default
        let labObservationsByLabFillerId = labObservations.groupBy(group => group.LabFillerId);
        const displayedItems = !this.state.showMore ? labObservationsByLabFillerId.slice(0, 1) : labObservationsByLabFillerId;

        return (
            <div key={`labObsGroup_${this.props.id}_${key}`} className="padding-sm-bottom">
                <span className="bold">{name}</span>
                {displayedItems.map((_obxs, idx) => {
                    let obxs = _obxs.sortByProperties(obx => obx.ObservationSetId).sortByProperties(obx => obx.RequestSetId);
                    let mainObx = obxs[0];

                    let value = "";
                    let unit = "";
                    let notes = [];
                    let canImport = false;

                    if (obxs.length > 1 || options.showAsNote) {
                        // append extra obxs for microbiologie text format results
                        for (let obx of obxs) {
                            if (!IsNullOrWhiteSpace(obx.Text) && !IsNullOrWhiteSpace(obx.ValueText))
                                notes.push(`${obx.Text ?? ""} : ${obx.ValueText ?? ""}`);
                            notes.push(obx.Note?.trim() ?? "");
                        }
                    }
                    else {
                        // normal flow for single value observation
                        value = mainObx.ValueText ?? "";
                        unit = mainObx.UnitsIdentifier ?? "";
                        notes.push(mainObx.Note);

                        canImport = this.props.import
                            && obxs.length == 1
                            && options.isValidRegex
                            && new RegExp(options.isValidRegex).test(value);
                    }

                    let trimester = this.getTrimester(mainObx.CollectionDate);
                    let formattedTrimester = options?.displayTrimester
                        ? this.getFormattedTrimester(trimester) ?? ""
                        : "";

                    let formattedNotes = notes
                        .filter(note => !IsNullOrWhiteSpace(note))
                        .join("\n");

                    let formattedMainObx = (
                        <div>
                            <div className="flex-container" style={{ justifyContent: "spaceBetween", width: "100%" }}>
                                <div style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={`${value} ${unit}`}>
                                    {`${value} ${unit}`}
                                </div>
                                <div style={{ minWidth: "78px", textAlign: "right" }}>{`${DateHelper.formatDate(mainObx.CollectionDate, "YYYY-MM-DD")}`}</div>
                            </div>
                        </div>
                    );

                    return (
                        <div key={`labObs_${this.props.id}_${key}_${idx}`} className="padding-xs-bottom">
                            <div>
                                {canImport
                                    ? (
                                        <div>
                                            <a onClick={() => this.props.import(mainObx, options, trimester)}>{formattedMainObx}</a>
                                            <div className="pull-right" style={{ fontSize: "12px", marginTop: "-4px" }}>
                                                {formattedTrimester != null &&
                                                    <div><span>{formattedTrimester}</span></div>
                                                }
                                            </div>
                                        </div>
                                    )
                                    : (
                                        <div>
                                            <div>{formattedMainObx}</div>
                                            <div className="pull-right" style={{ fontSize: "12px", marginTop: "-4px" }}>
                                                {formattedTrimester != null &&
                                                    <div><span>{formattedTrimester}</span></div>
                                                }
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                            {!IsNullOrWhiteSpace(formattedNotes) &&
                                <div style={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}><span>{`${formattedNotes}`}</span></div>
                            }

                            {labObservationsByLabFillerId?.length > 1 && idx == (displayedItems.length - 1) && (
                                <a onClick={() => this.onHandleMoreResult()}>
                                    {this.getString(!this.state.showMore ? "SeeMoreLabel" : "SeeLessLabel")}
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    public getTrimester(collectionDate: Date): number {
        if (collectionDate == null || this.props.finalEDB == null)
            return null;

        let diffweeks = 42 - LuxonDateHelper.dateDiff(collectionDate, this.props.finalEDB, "weeks");
        if (diffweeks < 0)
            return 0;
        if (diffweeks < 14)
            return 1;
        if (diffweeks >= 14 && diffweeks <= 27)
            return 2;
        if (diffweeks > 27)
            return 3;
    }

    public getFormattedTrimester(trimester: number): JSX.Element {
        if (trimester == null)
            return null;

        if (trimester == 1)
            return (<span>1<sup>st</sup> Trim.</span>);
        if (trimester == 2)
            return (<span>2<sup>nd</sup> Trim.</span>);
        if (trimester == 3)
            return (<span>3<sup>rd</sup> Trim.</span>);
    }

    private onHandleMoreResult(): void {
        this.mutateState(s => s.showMore = !s.showMore);
    }
}

export { ObstetricalFileLabObservationsDetails, ObstetricalFileLabObservationsDetailsProps };
